import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import type {CopilotChatState} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import {getSelectedThread} from '@github-ui/copilot-chat-state/get-selected-thread'
import {type AgentDropdownRepository, ModeSupportName} from '@github-ui/copilot-chat-types'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {useNavigate} from '@github-ui/use-navigate'
import type {Icon} from '@primer/octicons-react'
import {PencilIcon, PlusIcon, TrashIcon} from '@primer/octicons-react'

import type {AvailableCommand} from '../hooks/use-available-commands'
import type {CopilotChatManager} from './copilot-chat-manager'

interface SlashCommandParams {
  manager: CopilotChatManager
  chatState: CopilotChatState
  availableCommands: AvailableCommand[]
}

export type SlashCommand = {
  action: (
    params: string,
    state: CopilotChatState,
    navigate: ReturnType<typeof useNavigate>,
  ) => Promise<boolean> | boolean
  label?: string
  key: string // used for search
  icon?: Icon
}

export const SLASH_COMMAND_PREFIX = '/'

export const getSlashCommands = ({manager, chatState, availableCommands}: SlashCommandParams): SlashCommand[] => {
  const slashCommands: SlashCommand[] =
    chatState.mode === 'assistive'
      ? [
          {
            label: 'Delete chat',
            key: 'delete',
            icon: TrashIcon,
            action: async (_params, state) => {
              manager.dispatch({type: 'SLASH_COMMANDS_LOADING'})
              try {
                const selectedThread = getSelectedThread(state)
                if (!selectedThread) throw new Error('Thread not found')
                await manager.deleteThread(selectedThread)
                sendEvent('dotcom_chat.activate', {target: 'SLASH_COMMAND_DELETE_THREAD', mode: state.mode})
                manager.dispatch({type: 'SLASH_COMMANDS_LOADED'})
              } catch {
                manager.dispatch({type: 'SLASH_COMMANDS_ERROR'})
              }
              return true
            },
          },
          {
            label: 'Rename conversation',
            key: 'rename',
            icon: PencilIcon,
            action: async (params, state) => {
              manager.dispatch({type: 'SLASH_COMMANDS_LOADING'})
              try {
                const selectedThread = getSelectedThread(state)
                if (!selectedThread) throw new Error('Thread not found')
                await manager.renameThread(selectedThread, params)
                sendEvent('dotcom_chat.activate', {target: 'SLASH_COMMAND_RENAME', mode: state.mode})
                manager.dispatch({type: 'SLASH_COMMANDS_LOADED'})
              } catch {
                manager.dispatch({type: 'SLASH_COMMANDS_ERROR'})
              }
              return true
            },
          },
          {
            label: 'Start a new conversation',
            key: 'new',
            icon: PlusIcon,
            action: async (_params, state) => {
              manager.dispatch({type: 'SLASH_COMMANDS_LOADING'})
              try {
                await manager.selectThread(null)
                sendEvent('dotcom_chat.activate', {target: 'SLASH_COMMAND_NEW_THREAD', mode: state.mode})
                manager.dispatch({type: 'SLASH_COMMANDS_LOADED'})
              } catch {
                manager.dispatch({type: 'SLASH_COMMANDS_ERROR'})
              }
              return true
            },
          },
        ]
      : []

  if (chatState.mode === 'immersive') {
    for (const command of availableCommands) {
      if (command.execute) {
        slashCommands.push({
          key: command.key,
          action: async (params, state, navigate) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return await command.execute!(params, state, navigate, manager)
          },
        })
      }
    }
  }

  return slashCommands
}

export interface SlashCommandContext {
  selectedRepo?: AgentDropdownRepository
  selectedBranch?: string
  selectedAgent?: SelectedAgent
  selectedModelId?: string
  selectedReasoningEffort?: ReasoningEffort | null
  /** Serialized file/snippet reference URLs to include in the agent task prompt */
  references?: string[]
}

export const executePotentialSlashCommand = async (
  input: string,
  state: CopilotChatState,
  manager: CopilotChatManager,
  navigate: ReturnType<typeof useNavigate>,
  availableCommands: AvailableCommand[],
  activeModeName?: string,
  context?: SlashCommandContext,
): Promise<boolean> => {
  const isAgentMode = activeModeName === ModeSupportName.Agent

  if (!isAgentMode) {
    if ((input[0] ?? '') !== SLASH_COMMAND_PREFIX) return false
    // We know the first char is SLASH_COMMAND_PREFIX so we can safely remove it
    input = input.substring(1)
  }

  const slashCommands = getSlashCommands({manager, chatState: state, availableCommands})
  const matchingCommands = isAgentMode
    ? slashCommands.filter(slashCommand => slashCommand.key.toLowerCase() === activeModeName?.toLowerCase())
    : slashCommands.filter(slashCommand => {
        const firstInputWord = (input.split(' ', 1)[0] ?? '').toLowerCase().trim()
        return slashCommand.key.toLowerCase() === firstInputWord
      })

  if (matchingCommands.length !== 1) return false
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const matchingCommand = matchingCommands[0]!

  let params = isAgentMode ? input.trim() : input.substring(matchingCommand.key.length).trim()

  // If context is provided, wrap params in JSON with context
  if (
    context &&
    (context.selectedRepo ||
      context.selectedBranch ||
      context.selectedAgent ||
      context.selectedModelId ||
      context.selectedReasoningEffort !== undefined ||
      context.references?.length)
  ) {
    const payload = {
      text: params,
      selectedRepo: context.selectedRepo,
      selectedBranch: context.selectedBranch,
      selectedAgent: context.selectedAgent,
      model: context.selectedModelId ? {id: context.selectedModelId} : undefined,
      references: context.references,
    }
    params = JSON.stringify({...payload, ['reasoning_effort']: context.selectedReasoningEffort})
  }

  sendEvent('dotcom_chat.activate', {
    target: 'SLASH_COMMAND_EXECUTED',
    mode: state.mode,
    command: matchingCommand.key,
    activeModeName,
  })

  return await matchingCommand.action(params, state, navigate)
}
